import { Crown, Target, Trophy, Zap } from 'lucide-react'

// Tournament-bracket glyph: 4 seeds on the left collapsing through two
// semifinals to a single winner on the right. Styled to match Lucide
// (24x24 viewBox, 2px stroke, round caps).
function BracketIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M2 4h4v5H2" />
      <path d="M2 15h4v5H2" />
      <path d="M6 6.5h5v11h-5" />
      <path d="M11 12h4" />
    </svg>
  )
}

/**
 * EntryStatsRow - Shared inline metrics row for entry-level stats.
 *
 * Renders a consistent row of icon+value pairs for potential points,
 * accuracy, and champion status across BracketEntryCard, BracketPicks,
 * and LeaderboardTable.
 *
 * Props:
 * - potentialPoints: number | null — displayed as "+{value}"
 * - accuracy: number | null — displayed as "{value}%" or "—" when null
 * - accuracyNullDisplay: "zero" | "dash" — "zero" shows "0.0%", "dash" shows "—"
 * - champion: { label, status, name? } | null — label is "Active"/"Won"/"Eliminated", status is raw
 *   value for color; when name (the picked champ) is present it replaces the label, truncated, with
 *   the full "name — label" in the title tooltip (ticket 8fc2e125)
 * - championRatio: { alive, total } | null — champions still alive out of total champion picks (for LeaderboardTable)
 * - muted: boolean — when true, all icons become text-muted-foreground (for needsPicks variant)
 * - size: "xs" | "sm" — controls text size (xs default, sm for LeaderboardTable desktop)
 * - wrap: boolean — flex-wrap vs whitespace-nowrap
 */
export default function EntryStatsRow({
  potentialPoints,
  accuracy,
  accuracyNullDisplay = 'zero',
  champion,
  championRatio,
  tournamentsEntered,
  championshipsWon,
  muted = false,
  size = 'xs',
  wrap = false,
}) {
  const textSize = size === 'sm' ? 'text-sm' : 'text-xs'
  const wrapClass = wrap ? 'flex-wrap' : 'whitespace-nowrap'

  const zapColor = muted ? 'text-muted-foreground' : 'text-[var(--orange-9)]'
  const targetColor = muted ? 'text-muted-foreground' : 'text-[var(--blue-9)]'

  // Champion pick reads green while still in play (active or won) and red once
  // eliminated — icon and text share the color so the status is legible at a
  // glance on the leaderboard and division views.
  const getChampionTextColor = (status) => {
    if (muted) return 'text-muted-foreground'
    if (status === 'eliminated') return 'text-[var(--color-pick-wrong-text)]'
    return 'text-[var(--color-success)]'
  }

  const getChampionIconColor = (status) => {
    if (muted) return 'text-muted-foreground'
    if (status === 'eliminated') return 'text-[var(--color-pick-wrong-text)]'
    return 'text-[var(--color-success)]'
  }

  const formatAccuracy = () => {
    if (accuracy === null || accuracy === undefined) {
      return accuracyNullDisplay === 'dash' ? '—' : '0.0%'
    }
    return `${Number(accuracy).toFixed(1)}%`
  }

  return (
    <div className={`flex items-center gap-3 ${textSize} ${wrapClass} text-muted-foreground`}>
      {potentialPoints !== null && potentialPoints !== undefined && (
        <span className="flex items-center gap-1" title="Potential points">
          <Zap className={`h-3.5 w-3.5 ${zapColor}`} />
          +{potentialPoints.toLocaleString()}
        </span>
      )}
      {accuracy !== undefined && (
        <span className="flex items-center gap-1" title="Accuracy">
          <Target className={`h-3.5 w-3.5 ${targetColor}`} />
          {formatAccuracy()}
        </span>
      )}
      {champion && (
        <span
          className={`flex items-center gap-1 min-w-0 ${getChampionTextColor(champion.status)}`}
          title={champion.name ? `${champion.name} — ${champion.label}` : champion.label}
        >
          {/* Won swaps the crown for a trophy so the champ's name still reads
              as "won it all" without the word. Eliminated names strike through
              so red-green colorblind users get a non-color signal too. */}
          {champion.name && champion.status === 'won' ? (
            <Trophy className={`h-3.5 w-3.5 shrink-0 ${getChampionIconColor(champion.status)}`} />
          ) : (
            <Crown className={`h-3.5 w-3.5 shrink-0 ${getChampionIconColor(champion.status)}`} />
          )}
          {champion.name ? (
            <span className={`truncate max-w-28 sm:max-w-40 ${champion.status === 'eliminated' ? 'line-through' : ''}`}>
              {champion.name}
            </span>
          ) : (
            champion.label
          )}
        </span>
      )}
      {/* DUB-146: a 0/0 champion ratio is meaningless (pool play has no
          champion pick) — hide it rather than render "0/0". */}
      {championRatio && championRatio.total > 0 && (
        <span className="flex items-center gap-1">
          <Crown className={`h-3.5 w-3.5 ${muted ? 'text-muted-foreground' : 'text-[var(--color-crown)]'}`} />
          {championRatio.alive}/{championRatio.total}
        </span>
      )}
      {tournamentsEntered !== null && tournamentsEntered !== undefined && (
        <span className="flex items-center gap-1" title="Brackets submitted">
          <BracketIcon className={`h-3.5 w-3.5 ${muted ? 'text-muted-foreground' : 'text-[var(--magenta-9)]'}`} />
          {tournamentsEntered.toLocaleString()}
        </span>
      )}
      {championshipsWon !== null && championshipsWon !== undefined && (
        <span className="flex items-center gap-1" title="Champions picked">
          <Crown className={`h-3.5 w-3.5 ${muted ? 'text-muted-foreground' : 'text-[var(--color-crown)]'}`} />
          {championshipsWon.toLocaleString()}
        </span>
      )}
    </div>
  )
}
